import React from "react";
import { SERVICES, PROCESS_STEPS } from "../data";
import { SectionLabel, SectionTitle, SectionSub } from "../components/ui/Typography";
import CTABanner from "../components/ui/CTABanner";
import useScrollFade from "../hooks/useScrollFade";

const ServiceCard = ({ svc, idx }) => {
  const ref = useScrollFade();
  return (
    <div
      ref={ref}
      className="fade-in-section card-hover bg-white border border-slate-200 rounded-2xl p-7 flex gap-5"
      style={{ transitionDelay: `${idx * 70}ms` }}
    >
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${svc.gradient} flex items-center justify-center text-xl shrink-0 shadow-md`}>
        {svc.icon}
      </div>
      <div>
        <h3 className="font-head font-bold text-slate-900 text-base mb-2">{svc.title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed">{svc.desc}</p>
      </div>
    </div>
  );
};

const ProcessStep = ({ step, isLast }) => {
  const ref = useScrollFade();
  return (
    <div ref={ref} className="fade-in-section text-center relative">
      {!isLast && (
        <div className="hidden lg:block absolute top-7 left-1/2 w-full h-0.5 bg-slate-200 z-0" style={{ left: "60%" }} />
      )}
      <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 font-head font-black text-lg text-white relative z-10
        ${isLast ? "bg-orange-500" : "bg-blue-600"}`}>
        {step.num}
      </div>
      <h3 className="font-head font-bold text-slate-900 mb-2">{step.title}</h3>
      <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
    </div>
  );
};

const ServicesPage = ({ navigate }) => (
  <div className="page-enter">
    {/* Hero */}
    <section className="bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden pt-32 pb-20 text-center">
      <div className="hero-grid-bg absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
        <SectionLabel light>Our Services</SectionLabel>
        <h1 className="font-head font-black text-5xl md:text-6xl text-white mt-3 mb-5 tracking-tight leading-tight">
          End-to-End <span className="gradient-text">AI Services</span>
        </h1>
        <p className="text-white/60 text-xl">
          From strategy to deployment — we cover every stage of your AI transformation journey.
        </p>
      </div>
    </section>

    {/* Services grid */}
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {SERVICES.map((svc, i) => <ServiceCard key={svc.title} svc={svc} idx={i} />)}
        </div>
      </div>
    </section>

    {/* Process */}
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <SectionLabel>Our Process</SectionLabel>
          <SectionTitle>How We <span className="text-blue-600">Deliver</span></SectionTitle>
          <SectionSub center>
            A structured, agile approach that gets you from concept to production quickly and efficiently.
          </SectionSub>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
          {PROCESS_STEPS.map((step, i) => (
            <ProcessStep key={step.num} step={step} isLast={i === PROCESS_STEPS.length - 1} />
          ))}
        </div>
      </div>
    </section>

    {/* Why us mini strip */}
    <section className="py-16 bg-gradient-to-br from-slate-950 to-[#0d2151]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: "🏆", title: "50+", sub: "Projects Delivered" },
            { icon: "🤝", title: "30+", sub: "Enterprise Clients" },
            { icon: "⚡", title: "60d", sub: "Average MVP Time" },
            { icon: "💯", title: "98%", sub: "Client Satisfaction" },
          ].map(s => (
            <div key={s.title} className="text-center glass rounded-2xl p-6">
              <div className="text-3xl mb-3">{s.icon}</div>
              <div className="font-head font-black text-3xl text-white mb-1">{s.title}</div>
              <div className="text-white/45 text-sm">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <CTABanner
      navigate={navigate}
      title="Let's Build Your AI Solution"
      sub="Describe your challenge and we'll scope the solution — free, no commitment."
      btn1="Start a Project"
      btn2={null}
      page1="contact"
    />
  </div>
);

export default ServicesPage;
